import { useCallback, useEffect, useState } from "react";
import { useWoStatuses, woStatusLabel, woStatusOf, woStatusStyle } from "utils/woStatus";
import WoStatusOptions from "components/WoStatusOptions";
import RefreshButton from "components/RefreshButton/RefreshButton";
import { Modal } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";

import QuickViewWorkOrder from "components/QuickViewWorkOrder/QuickViewWorkOrder";
import API from "services/axios";
import authHeader from "services/auth-header";
import { setNotification } from "features/Notification/notificationSlice";
import { useDispatch } from "react-redux";
import Spinner from "components/Spinner";
import useWorkOrder from "features/Contexts/WorkOrderContext";
import TableOnly from "components/DataTable/TableOnly";
import PaginationWithState from "components/Pagination/PaginationWithState";

function CustomerWorkOrder({ selectedCustomer }) {
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { clearCustomerData, customerContextData } = useWorkOrder();

  /*   All States
   ********************************************* */
  const [filterBy, setFilterBy] = useState(null);
  const [selectedTab, setSelectedTab] = useState("all");
  const [customerWO, setCustomerWO] = useState(null);
  const [loading, setLoading] = useState(null);
  const [showQuickViewModal, setShowQuickViewModal] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showJobNameModal, setShowJobNameModal] = useState(false);

  // Bulk status update (Processing tab). `totalCount` is what the server says
  // matches the current tab + search -- NOT customerWO.length, which is only
  // the page on screen, and would understate what the button is about to do.
  const [totalCount, setTotalCount] = useState(0);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("completed");
  const [bulkUpdating, setBulkUpdating] = useState(false);
  // Which tab the modal was opened for. Double-clicking a tab you are not on
  // switches tabs first, and the refetch is async -- until it lands, the count
  // on screen still belongs to the previous tab, so confirming is blocked.
  const [bulkTab, setBulkTab] = useState(null);
  const [jobNamesFormData, setJobNamesFormData] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  /*   All Functions
   ********************************************* */
  // Opened by double-clicking the Pending or Processing tab. `tab` is recorded
  // so the modal can name the tab it is about to rewrite, and so a stale
  // double click cannot act on a tab the user has since moved away from.
  const openBulkStatusModal = (tab) => {
    setBulkTab(tab);
    // Completed when the company still has it, else its first status.
    setBulkStatus(
      woStatusOf(woStatuses, "completed") ? "completed" : woStatuses[0]?.value || "completed",
    );
    setShowBulkStatusModal(true);
  };

  // Set the status of every work order in the current tab.
  //
  // The ids are NOT sent. The server re-runs the same filter the list uses, so
  // this covers every matching row rather than the page in the browser, and an
  // active search narrows it identically. Sending ids would quietly update 10
  // of 200 and still report success.
  const handleBulkStatusUpdate = async () => {
    try {
      setBulkUpdating(true);
      const { data } = await API.patch(
        "customer/work-orders/bulk-status",
        {
          id: selectedCustomer?.id,
          // bulkTab, not selectedTab: send the tab the modal actually named.
          // They are equal whenever confirm is enabled, but tying the write to
          // the sentence the user read is what keeps it that way.
          status: bulkTab,
          query: searchTerm,
          filterBy: searchTerm && searchTerm !== "" ? filterBy : undefined,
          WOStatus: bulkStatus,
        },
        { headers: authHeader() },
      );

      if (!data?.success) {
        throw new Error(data?.message || "Bulk update failed");
      }

      setShowBulkStatusModal(false);
      dispatch(
        setNotification({
          message: data.message || `${data.updated} work orders updated`,
          type: "success",
        }),
      );
      setPageNumber(1);
      await getCustomerWOs(false, 1);
    } catch (err) {
      dispatch(
        setNotification({
          message: err?.response?.data?.message || err.message,
          type: "error",
        }),
      );
    } finally {
      setBulkUpdating(false);
    }
  };

  // Get Customer Work Order
  const getCustomerWOs = async (clearSearch = false, page = 1) => {
    try {
      setLoading(true);

      const { data } = await API.post(
        "customer/work-order?page=" + page + "&limit=" + pageLimit,
        {
          id: selectedCustomer?.id,
          status: selectedTab,
          query: clearSearch ? "" : searchTerm,
          filterBy: searchTerm && searchTerm !== "" ? filterBy : undefined,
        },
        { headers: authHeader() },
      );

      if (!data.success) {
        throw new Error(data.message || "Failed to fetch work orders");
      }
      setCustomerWO(data.data);
      setTotalPages(data.totalPages);
      setTotalCount(data.total ?? 0);

      return data;
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  // Getting 12 Hours Time from SQL timestamp
  const convertSqlTimestampToDate = (sqlTime) => {
    const timestamp = sqlTime;
    const date = new Date(timestamp);
    const time = date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    return time;
  };

  const setWorkOrderContext = async (selectedWorkOrder) => {
    const ids = customerWO ? customerWO.map((w) => w.woId) : [];
    return navigate(
      `/work-order/details/${selectedWorkOrder.WOStatus}/${selectedWorkOrder?.woId}?page=1`,
      { state: { workOrderIds: ids } },
    );
  };

  // Badge colour + name from Settings -> WO Statuses (user, 2026-10-29).
  const woStatuses = useWoStatuses();

  const getApprovalStatus = (item) => {
    const hasRejected =
      item?.woLotApproval?.hasRejected ||
      item?.woLotApproval?.excelFileStatus === "rejected" ||
      item?.woLotApproval?.shippingAddressStatus === "rejected";

    if (hasRejected) {
      return {
        text: "Rejected",
        className: "bg-danger",
      };
    }

    if (item?.woLotApproval?.allApproved) {
      return {
        text: "Approved",
        className: "bg-success",
      };
    }

    if (
      item?.woLotApproval?.approvalEmailSent &&
      (item?.woLotApproval?.excelFileStatus !== "pending_approval" ||
        item?.woLotApproval?.shippingAddressStatus !== "pending_approval" ||
        item?.woLotApproval?.inHandByDate !== null)
    ) {
      return {
        text: "Partially",
        className: "border border-success border-3 text-dark",
        style: { backgroundColor: "#ffff00" },
      };
    }

    if (!item?.woLotApproval?.approvalEmailSent) {
      return {
        text: "Not sent",
        className: "bg-secondary",
      };
    }

    return {
      text: "Pending",
      className: "bg-info",
    };
  };

  const updateSearchParams = (key, value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }

      return newParams.toString();
    });
  };

  /*   Table Functions
   ********************************************* */
  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      {/* FilterBy */}
      <button
        type="button"
        className="px-1 text-nowrap bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy
          ? filterBy.toUpperCase().replaceAll("_", " ")
          : "Filter by All"}
      </button>
      <div className="dropdown-menu">
        <button onClick={() => setFilterBy(null)} className="dropdown-item">
          Filter by All
        </button>
        <button
          onClick={() => setFilterBy("customerName")}
          className="dropdown-item"
        >
          Customer Name
        </button>
        <button
          onClick={() => setFilterBy("jobName")}
          className="dropdown-item"
        >
          Job Name
        </button>
        <button
          onClick={() => setFilterBy("subJobName")}
          className="dropdown-item"
        >
          Sub Job Name
        </button>
        <button
          onClick={() => setFilterBy("WOStatus")}
          className="dropdown-item"
        >
          WO Status
        </button>
      </div>
    </div>
  );

  const table_head = [
    { name: "W-ID", value: "woId" },
    // { name: "Customer Name", value: "customerName" },
    { name: "Job Name", value: "jobName" },
    { name: "Sub Job Name", value: "subJobName" },
    { name: "Created Date", value: "createdAt" },
    { name: "Created By", value: "createdBy" },
    { name: "Invoiced", value: "invoiceReady" },
    { name: "Status", value: "WOStatus" },
    { name: "Approval", value: "approvalStatus" },
    { name: "Actions", value: "" },
  ];

  const table_body = (item, index) => {
    const approvalStatus = getApprovalStatus(item);

    return (
      <tr className="" key={item.id}>
        <td className="py-1 text-nowrap">W-{item.woId}</td>
        {/* <td className="py-1">{item.customerName}</td> */}
        <td
          onDoubleClick={() => handleJobNameDoubleClick(item)}
          className="py-1"
        >
          {item.jobName}
        </td>
        <td
          onDoubleClick={() => handleJobNameDoubleClick(item)}
          className="py-1"
        >
          {item.subJobName}
        </td>
        <td className="py-1">{convertSqlTimestampToDate(item.createdAt)} </td>
        <td className="py-1">{item.createdBy} </td>
        <td className="py-1">{item.invoiceReady ? "Yes" : "No"} </td>
        <td className="py-1">
          <span
            className="badge px-2 py-1"
            style={woStatusStyle(woStatuses, item?.WOStatus)}
          >
            {woStatusLabel(woStatuses, item?.WOStatus)}
          </span>
        </td>
        <td className="py-1">
          <span
            className={`badge px-2 py-1 ${approvalStatus.className}`}
            style={approvalStatus.style}
          >
            {approvalStatus.text}
          </span>
        </td>
        <td className="table-action py-1 d-flex justify-content-center align-items-center">
          <button
            onClick={(e) => {
              setShowQuickViewModal(true);
              updateSearchParams("selected-work-order", item?.woId);
            }}
            className="btn btn-sm bg-success text-nowrap text-white"
          >
            Quick View
          </button>
          <button
            onClick={() => {
              setWorkOrderContext(item);
            }}
            className={`btn btn-sm bg-success text-white mx-2 `}
          >
            Details
          </button>
        </td>
      </tr>
    );
  };

  const handleJobNameDoubleClick = (item) => {
    setSelectedItem(item);
    setJobNamesFormData({
      jobName: item.jobName,
      subJobName: item.subJobName,
    });
    setShowJobNameModal(true);
  };

  const UpdateJobNames = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await API.patch(
        "/work-orders",
        {
          data: {
            jobName: jobNamesFormData.jobName,
            subJobName: jobNamesFormData.subJobName,
          },
          id: selectedItem.id,
        },
        { headers: authHeader() },
      );

      if (data?.message?.type === "success") {
        await getCustomerWOs(true, pageNumber);
        setShowJobNameModal(false);
        setLoading(false);
        return;
      }
      setShowJobNameModal(false);
      setLoading(false);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setLoading(false);
    }
  };

  const refreshCurrentPage = useCallback(
    (clearSearch = false) => getCustomerWOs(clearSearch, pageNumber),
    [
      pageNumber,
      pageLimit,
      selectedTab,
      searchTerm,
      filterBy,
      selectedCustomer?.id,
    ],
  );

  useEffect(() => {
    let timeout = setTimeout(() => {
      getCustomerWOs(true, pageNumber);
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedCustomer, selectedTab, pageLimit, pageNumber]);

  useEffect(() => {
    return () => {
      clearCustomerData();
    };
  }, []);

  return (
    <div
      className="pt-3 px-3 horizontal-scrollable d-flex flex-grow-1 flex-column gap-3"
      style={{ borderRadius: 10 }}
    >
      {/* Spinner */}
      {loading && <Spinner />}
      {/* Tabs */}
      <div className="">
        <button
          className={`btn ${
            selectedTab === "all" ? "btn-primary" : "bg-light"
          }`}
          onClick={(e) => {
            setPageNumber(1);
            setSearchTerm("");
            setSelectedTab("all");
          }}
        >
          All
        </button>
        {/* <button
          className={`btn ${
            selectedTab === "p-p-r" ? "btn-primary" : "bg-light"
          }`}
          onClick={(e) => {
            setPageNumber(1);
            setSearchTerm("");
            setSelectedTab("p-p-r");
          }}
        >
          P-P-R
        </button>
        <button
          className={`btn ${
            selectedTab === "readyToBill" ? "btn-primary" : "bg-light"
          }`}
          onClick={(e) => {
            setSearchTerm("");
            setPageNumber(1);
            setSelectedTab("readyToBill");
          }}
        >
          Ready
        </button> */}
        <button
          className={`btn ${
            selectedTab === "invoice-ready" ? "btn-primary" : "bg-light"
          }`}
          onClick={(e) => {
            setSearchTerm("");
            setPageNumber(1);
            setSelectedTab("invoice-ready");
          }}
        >
          Pending Invoices
        </button>

        {/* Pending and Processing open the bulk status modal on DOUBLE click.
            The first click of that double click selects the tab as normal, so
            double-clicking a tab you are not on switches to it first and the
            modal then describes THAT tab -- which is why the modal reads its
            count from state rather than being passed one here, and why its
            confirm button stays disabled until the tab's fetch settles. */}
        <button
          className={`btn ${
            selectedTab === "pending" ? "btn-primary" : "bg-light"
          }`}
          title="Double-click to bulk update the status of every order in this tab"
          onClick={(e) => {
            setPageNumber(1);
            setSearchTerm("");
            setSelectedTab("pending");
          }}
          onDoubleClick={() => openBulkStatusModal("pending")}
        >
          Pending
        </button>
        <button
          className={`btn ${
            selectedTab === "processing" ? "btn-primary" : "bg-light"
          }`}
          title="Double-click to bulk update the status of every order in this tab"
          onClick={(e) => {
            setPageNumber(1);
            setSearchTerm("");
            setSelectedTab("processing");
          }}
          onDoubleClick={() => openBulkStatusModal("processing")}
        >
          Processing
        </button>
      </div>

      <div>
        {/* Limit and Search */}

        <div className="d-flex justify-content-between mb-3 align-items-center">
          {/* Limit */}
          <div>
            Show &nbsp;
            <div className="btn-group">
              <button
                className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {pageLimit}
              </button>
              <ul className="dropdown-menu w-25">
                <li
                  onClick={() => {
                    setPageLimit(5);
                  }}
                >
                  <button className="dropdown-item" href="#">
                    5
                  </button>
                </li>
                <li
                  onClick={() => {
                    setPageLimit(10);
                  }}
                >
                  <button className="dropdown-item" href="#">
                    10
                  </button>
                </li>
                <li
                  onClick={() => {
                    setPageLimit(15);
                  }}
                >
                  <button className="dropdown-item" href="#">
                    15
                  </button>
                </li>
                <li
                  onClick={() => {
                    setPageLimit(20);
                  }}
                >
                  <button className="dropdown-item" href="#">
                    20
                  </button>
                </li>
              </ul>
            </div>
          </div>
          {/* Search */}
          <div className="d-flex">
            {buttons()}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setPageNumber(1);
                if (searchTerm && searchTerm !== "") {
                  getCustomerWOs();
                }
              }}
              className="d-flex justify-content-end gap-1 align-items-center"
            >
              <input
                type="search"
                placeholder="Search..."
                value={searchTerm}
                className="form-control"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="btn btn-primary" type="submit">
                Search
              </button>
              <RefreshButton
                className="btn btn-outline-primary"
                onClick={() => getCustomerWOs(false, 1)}
              />
              {searchTerm !== "" && (
                <button
                  className="btn btn-outline-danger"
                  type="button"
                  onClick={() => {
                    setSearchTerm((prev) => "");
                    getCustomerWOs(true);
                  }}
                >
                  Clear
                </button>
              )}
            </form>
          </div>
        </div>
        {/* Table */}
        <TableOnly
          table_body={table_body}
          table_data={customerWO}
          table_head={table_head}
          disableSorting={true}
        />
        {/* Pagination */}
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      <QuickViewWorkOrder
        refreshFunc={refreshCurrentPage}
        hideCustomerViewBtn={true}
        // selectedId={item?.woId}
        show={showQuickViewModal}
        setShow={setShowQuickViewModal}
        updateSearchParams={updateSearchParams}
        workOrdersList={customerWO}
        disabledInvoice={false}
      />
      {/* Bulk status update */}
      <Modal
        show={showBulkStatusModal}
        onHide={() => !bulkUpdating && setShowBulkStatusModal(false)}
      >
        <Modal.Body>
          {bulkUpdating && <Spinner />}
          <h4>Bulk Update Status</h4>
          <hr />
          {/* `ready` means the list showing behind the modal is the one the
              modal is talking about. Until then the count belongs to the tab
              we just left, and showing it would misstate what is about to be
              rewritten. */}
          {!(selectedTab === bulkTab && !loading) ? (
            <p className="mb-2">Counting the orders in this tab…</p>
          ) : (
            <>
              <p className="mb-2">
                This will set the status of{" "}
                <strong>
                  {totalCount} work order{totalCount === 1 ? "" : "s"}
                </strong>{" "}
                in the{" "}
                <strong>
                  {bulkTab === "pending" ? "Pending" : "Processing"}
                </strong>{" "}
                tab
                {searchTerm ? (
                  <>
                    {" "}
                    matching your search <strong>“{searchTerm}”</strong>
                  </>
                ) : null}
                .
              </p>
              <p className="text-muted small">
                Every matching work order is updated, not just the ones on this
                page. This cannot be undone in bulk.
              </p>
            </>
          )}

          <div className="my-3">
            <label className="form-label">New status</label>
            <select
              className="form-select"
              value={bulkStatus}
              disabled={bulkUpdating}
              onChange={(e) => setBulkStatus(e.target.value)}
            >
              {/* Settings -> WO Statuses (2026-10-29) */}
              <WoStatusOptions />
            </select>
          </div>

          <hr />
          <div className="d-flex justify-content-end gap-1">
            <button
              className="btn btn-secondary"
              disabled={bulkUpdating}
              onClick={() => setShowBulkStatusModal(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={
                bulkUpdating ||
                loading ||
                selectedTab !== bulkTab ||
                totalCount === 0
              }
              onClick={handleBulkStatusUpdate}
            >
              {bulkUpdating ? "Updating..." : `Update ${totalCount}`}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        size="lg"
        show={showJobNameModal}
        onHide={() => setShowJobNameModal(false)}
      >
        <Modal.Body>
          <form onSubmit={UpdateJobNames}>
            {loading && <Spinner />}
            <h4>Update Job Name / Sub Job Name for W-{selectedItem?.woId}</h4>
            <hr />
            <div className="my-3">
              <label>Job Name</label>
              <input
                className="form-control"
                value={jobNamesFormData?.jobName || ""}
                onChange={(e) =>
                  setJobNamesFormData((prev) => ({
                    ...prev,
                    jobName: e.target.value,
                  }))
                }
              />
            </div>
            <div className="my-3">
              <label>Sub Job Name</label>
              <input
                className="form-control"
                value={jobNamesFormData?.subJobName || ""}
                onChange={(e) =>
                  setJobNamesFormData((prev) => ({
                    ...prev,
                    subJobName: e.target.value,
                  }))
                }
              />
            </div>
            <hr className="mt-4" />
            <div className="text-end">
              <button
                type="button"
                className="btn btn-sm btn-secondary me-2"
                onClick={() => setShowJobNameModal(false)}
              >
                Close
              </button>
              <button className="btn btn-sm btn-primary">Update</button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default CustomerWorkOrder;
